import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Network,
  Shield,
  Server,
  Lock,
  ArrowRight,
  BookOpen,
  Mail,
  CheckCircle2,
  Router,
  Cloud,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const services = [
  {
    Icon: Network,
    title: "OT-Netzwerkarchitektur",
    text: "Entwurf belastbarer OT-Netzwerke für Gebäudeautomation und industrielle Anlagen: Segmentierung, Switching, Routing und Redundanz, ausgerichtet an IEC 62443-3-2 und den Anforderungen des laufenden Betriebs.",
  },
  {
    Icon: Router,
    title: "Gatewaylayer & Protokollübersetzung",
    text: "Planung und Bewertung von Gateway- und Protokollumsetzungsebenen zwischen BACnet, KNX, Modbus, LON und IP-basierten Systemen. Fokus auf Verfügbarkeit, Auditierbarkeit und kontrolliertem Datenfluss.",
  },
  {
    Icon: Shield,
    title: "Segmentierung & Firewalling",
    text: "Umsetzung von Zonen und Conduits in der Praxis: welche Firewalls, VLANs und Access Control wo Sinn machen, wo ein Gateway reicht und wie Sie Kosten und Komplexität im Griff behalten.",
  },
  {
    Icon: Cloud,
    title: "Sichere Fernwartung & Cloud-Anbindung",
    text: "Konzepte für Fernzugriff, VPN, Jump Hosts, Remote Maintenance und Cloud-Edge-Konnektivität, die OT-Verfügbarkeit und IT-Sicherheit gleichermaßen berücksichtigen.",
  },
];

const benefits = [
  "Sie bekommen eine Netzwerkarchitektur, die Sicherheit und Betriebsstabilität gleichermaßen unterstützt.",
  "Sie vermeiden teure Fehlinvestitionen in überdimensionierte Sicherheitslösungen.",
  "Sie schaffen klare Schnittstellen zwischen IT, OT, Facility und Integratoren.",
  "Sie erfüllen NIS-2, BSI INF.13/INF.14 und IEC 62443 mit einer durchgängigen Architekturlogik.",
  "Sie reduzieren Angriffsflächen durch kontrollierte Gateways statt flacher Netzwerke.",
];

const faqs = [
  {
    q: "Was versteht man unter OT-Netzwerkarchitektur?",
    a: "OT-Netzwerkarchitektur beschreibt das Zusammenspiel von Automatisierungsnetzwerken in Gebäuden und Anlagen: welche Komponenten in welchen Zonen kommunizieren, über welche Gateways und Firewalls Daten fließen und wie Fernzugriffe, Cloud-Dienste und IT-Systeme sicher angebunden werden.",
  },
  {
    q: "Was ist der Gatewaylayer in der Gebäudeautomation?",
    a: "Der Gatewaylayer bildet die Übersetzungsebene zwischen klassischen Feldbussen und Netzwerkprotokollen (zum Beispiel BACnet, KNX, Modbus, LON) und IP-basierten Systemen, Diensten oder Cloud-Plattformen. Er ist ein zentraler Hebel für Sicherheit, da hier Datenflüsse kontrolliert und protokolliert werden können.",
  },
  {
    q: "Wie hängen Gatewaylayer und IEC 62443 zusammen?",
    a: "IEC 62443-3-2 verlangt eine Risikobetrachtung auf Basis von Zonen und Conduits. Der Gatewaylayer ist oft der technische Ausdruck eines Conduits: er verbindet Zonen unterschiedlichen Vertrauensniveaus und muss deshalb geplant, abgesichert und überwacht werden.",
  },
  {
    q: "Wann reicht ein Gateway, wann brauche ich eine Firewall?",
    a: "Ein Gateway übersetzt Protokolle und steuert Datenflüsse auf Anwendungsebene. Eine Firewall schützt auf Netzwerkebene. In der Praxis ergänzen sie sich: zwischen OT- und IT-Netz sowie zum Internet kommen in der Regel beide zum Einsatz, innerhalb einer vertrauenswürdigen OT-Zone kann ein managed Switch mit ACLs ausreichen.",
  },
  {
    q: "Wie sichere ich Fernwartung für OT-Systeme?",
    a: "Sichere Fernwartung braucht mehr als ein VPN: gekapselte Zugänge, Multifaktor-Authentifizierung, Session-Logging, zeitlich begrenzte Freigaben, getrennte Berechtigungsrollen und ein Notfallkonzept. Ich entwickle ein Konzept, das für Ihre Liegenschaft oder Anlage wirtschaftlich und auditfähig ist.",
  },
  {
    q: "Wie starten wir ein Architekturprojekt?",
    a: "Der pragmatische Einstieg ist ein Architektur-Assessment: Bestandsaufnahme der Netzwerke, Protokolle, Schnittstellen und Zuständigkeiten. Daraus leiten wir ein Zielbild mit kurz-, mittel- und langfristigen Maßnahmen ab. Alternativ starten Sie mit dem FACHSPRECHPARTNER Workbook.",
  },
];

const OtNetworkArchitecture = () => {
  const openContact = () =>
    window.dispatchEvent(new CustomEvent("open-contact-form"));

  const canonical = "https://fachsprechpartner.de/ot-netzwerkarchitektur";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: "OT-Netzwerkarchitektur und Gatewaylayer für Gebäudeautomation",
        serviceType: "OT-Netzwerkberatung und Gatewaylayer-Design",
        provider: {
          "@type": "Organization",
          name: "FACHSPRECHPARTNER",
          url: "https://fachsprechpartner.de",
        },
        areaServed: { "@type": "Country", name: "Deutschland" },
        description:
          "Beratung zu OT-Netzwerkarchitektur und Gatewaylayer für Gebäudeautomation: Segmentierung, Protokollgateways, Firewalling, Fernwartung und Cloud-Anbindung nach IEC 62443 und NIS-2.",
        url: canonical,
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fachsprechpartner.de/" },
          { "@type": "ListItem", position: 2, name: "OT-Netzwerkarchitektur", item: canonical },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>OT-Netzwerkarchitektur & Gatewaylayer für Gebäudeautomation</title>
        <meta
          name="description"
          content="OT-Netzwerkarchitektur und Gatewaylayer für Gebäudeautomation: Segmentierung, Protokollgateways, Firewalling, Fernwartung und Cloud-Anbindung nach IEC 62443 und NIS-2."
        />
        <meta
          name="keywords"
          content="OT-Netzwerkarchitektur, Gatewaylayer, Gebäudeautomation, OT-Security, IEC 62443, BACnet Gateway, Fernwartung, OT Firewall, NIS-2"
        />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="de" href={canonical} />
        <link rel="alternate" hrefLang="x-default" href={canonical} />
        <meta property="og:title" content="OT-Netzwerkarchitektur & Gatewaylayer für Gebäudeautomation" />
        <meta
          property="og:description"
          content="OT-Netzwerkarchitektur und Gatewaylayer für Gebäudeautomation: Segmentierung, Protokollgateways, Firewalling, Fernwartung und Cloud-Anbindung."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="https://fachsprechpartner.de/og-image.webp" />
        <meta property="og:image:type" content="image/webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="FACHSPRECHPARTNER Tim Bauer, OT-Security und Gebäudeautomation" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:site_name" content="Fachsprechpartner" />
        <meta name="twitter:title" content="OT-Netzwerkarchitektur & Gatewaylayer für Gebäudeautomation" />
        <meta name="twitter:description" content="OT-Netzwerkarchitektur und Gatewaylayer für Gebäudeautomation: Segmentierung, Protokollgateways, Firewalling, Fernwartung und Cloud-Anbindung." />
        <meta name="twitter:image" content="https://fachsprechpartner.de/og-image.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-primary to-primary/90 text-primary-foreground">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-accent text-xs font-semibold uppercase tracking-wider mb-6">
                <Network className="w-4 h-4" />
                OT-Netzwerkarchitektur · Gatewaylayer
              </div>
              <h1 className="text-4xl md:text-6xl font-heading font-bold leading-tight mb-6">
                Architektur, die Sicherheit und Betrieb verbindet.
              </h1>
              <p className="text-lg md:text-xl text-primary-foreground/85 mb-8 leading-relaxed max-w-3xl">
                Ich plane OT-Netzwerkarchitekturen und Gatewaylayer für
                Gebäudeautomation und industrielle Umgebungen: von Segmentierung
                und Protokollübersetzung bis zu sicherer Fernwartung und
                Cloud-Anbindung, immer nach IEC 62443 und NIS-2 ausgerichtet.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={openContact}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
                >
                  Architektur-Assessment anfragen <ArrowRight className="w-4 h-4" />
                </button>
                <Link
                  to="/workbook"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 text-primary-foreground font-semibold hover:bg-primary-foreground/10 transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  Zum Workbook
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Leistungen */}
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Leistungen rund um OT-Netzwerkarchitektur
            </h2>
            <p className="text-muted-foreground mb-12 max-w-2xl">
              Vier Bausteine, die Ihre OT-Netzwerke belastbarer, sicherer und
              besser steuerbar machen, ohne den laufenden Betrieb zu gefährden.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map(({ Icon, title, text }) => (
                <div
                  key={title}
                  className="p-6 rounded-2xl border border-border bg-card hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-2">
                    {title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Nutzen */}
        <section className="py-16 md:py-24 bg-muted/40">
          <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Was Sie davon haben
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Eine gute OT-Netzwerkarchitektur ist kein Selbstzweck. Sie wird
                zum operativen Vorteil, wenn sie Verfügbarkeit, Sicherheit und
                Wartbarkeit gleichermaßen verbessert.
              </p>
            </div>
            <ul className="space-y-3">
              {benefits.map((b) => (
                <li key={b} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                  <span className="text-foreground/90">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8">
              Häufige Fragen zur OT-Netzwerkarchitektur
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left font-semibold">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Ihre OT-Architektur auf ein solides Fundament stellen
            </h2>
            <p className="text-primary-foreground/85 mb-8 leading-relaxed">
              Starten Sie mit einem Architektur-Assessment oder dem Workbook.
              In beiden Fällen bekommen Sie ein klares Zielbild und einen
              priorisierten Maßnahmenplan statt einer technischen Wunschliste.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/workbook"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                Workbook ansehen
              </Link>
              <button
                onClick={openContact}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 font-semibold hover:bg-primary-foreground/10 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Sparring anfragen
              </button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default OtNetworkArchitecture;
